import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Lock } from "lucide-react";
import { authService } from "../utils/auth";
import { usePermissions } from "../hooks/usePermissions";
import { SOLUTION_ROUTES } from "../config/solutions";
import AccessRequiredModal from "../components/AccessRequiredModal";

/**
 * SynexOS Home — content for the /home route.
 *
 * Rendered into the standard application shell (WorkspaceLayoutNew, homeMode) via <Outlet>,
 * so the sidebar, top bar, search, notifications and profile are the shell's, not this
 * page's. This file owns only the content area.
 *
 * Nothing here reads or changes authorization; it renders `hasSolution()` and nothing more.
 *
 * Adding a future solution card is ONE entry in config/solutions.js — the grid adapts.
 */

/** Placeholder cards. Presentational only; no routes, no registry entries, no APIs. */
const COMING_SOON = ["Procurement", "Finance", "CRM", "HR", "Inventory"];

export default function SynexosHome() {
  const navigate = useNavigate();
  const { hasSolution } = usePermissions();
  const [lockedSolution, setLockedSolution] = useState(null);

  const user = authService.getUser();
  const firstName = (user?.full_name || "").trim().split(" ")[0];

  return (
    <div className="w-full px-6 py-6 lg:px-8">
      {/* Page header — the shell shows no page-specific chrome in homeMode */}
      <header>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back{firstName ? `, ${firstName}` : ""}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          SynexOS is your enterprise operating system — one platform for how your
          organization runs its work.
        </p>
      </header>

      {/* About */}
      <section className="mt-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-base font-semibold text-gray-900">About SynexOS</h2>
        <p className="mt-2 max-w-4xl text-sm leading-relaxed text-gray-600">
          SynexOS is the enterprise operating system, bringing together collaboration,
          revenue, procurement and future enterprise solutions in a single platform. Your
          organization administrator decides which solutions you can open.
        </p>
      </section>

      {/* Solutions */}
      <section className="mt-8">
        <div className="flex items-baseline justify-between">
          <h2 className="text-base font-semibold text-gray-900">Solutions</h2>
          <span className="text-xs text-gray-500">
            {SOLUTION_ROUTES.filter((s) => hasSolution(s.key)).length} of{" "}
            {SOLUTION_ROUTES.length} available to you
          </span>
        </div>

        <div className="mt-4 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {SOLUTION_ROUTES.map((solution) => (
            <SolutionCard
              key={solution.key}
              solution={solution}
              granted={hasSolution(solution.key)}
              onOpen={() => navigate(solution.path)}
              onLocked={() => setLockedSolution(solution.name)}
            />
          ))}
        </div>
      </section>

      {/* Coming soon */}
      {/* <section className="mt-10">
        <h2 className="text-base font-semibold text-gray-900">
          Future Enterprise Solutions
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Joining the SynexOS platform over time.
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {COMING_SOON.map((name) => (
            <div
              key={name}
              className="rounded-xl border border-dashed border-gray-300 bg-white/60 p-5 text-center shadow-sm"
            >
              <p className="text-sm font-semibold text-gray-700">{name}</p>
              <span className="mt-2 inline-block rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">
                Coming Soon
              </span>
            </div>
          ))}
        </div>
      </section> */}

      {lockedSolution && (
        <AccessRequiredModal
          solutionName={lockedSolution}
          onClose={() => setLockedSolution(null)}
        />
      )}
    </div>
  );
}

function SolutionCard({ solution, granted, onOpen, onLocked }) {
  const Icon = solution.icon;

  return (
    <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between">
        <span
          className={`flex h-11 w-11 items-center justify-center rounded-xl ${
            granted ? "bg-blue-50 text-[#033F99]" : "bg-gray-100 text-gray-400"
          }`}
        >
          {Icon ? <Icon className="h-5 w-5" /> : null}
        </span>

        {granted ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
            <Check className="h-3 w-3" />
            Available
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
            <Lock className="h-3 w-3" />
            Access Required
          </span>
        )}
      </div>

      <h3 className="mt-4 text-lg font-semibold text-gray-900">{solution.name}</h3>
      <p className="mt-1 text-sm text-gray-600">{solution.description}</p>

      <ul className="mt-4 flex flex-wrap gap-1.5">
        {solution.features.map((feature) => (
          <li
            key={feature}
            className="rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-600"
          >
            {feature}
          </li>
        ))}
      </ul>

      <div className="flex-1" />

      {/* Same label in both states, per spec. Locked opens the modal; it never navigates. */}
      <button
        onClick={granted ? onOpen : onLocked}
        className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
          granted
            ? "bg-[#033F99] text-white hover:bg-[#022e73]"
            : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        }`}
      >
        {granted ? null : <Lock className="h-4 w-4" />}
        Open {solution.name}
        {granted ? <ArrowRight className="h-4 w-4" /> : null}
      </button>
    </div>
  );
}
